import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';

// Ícones
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-9-4l5-5m-5 5l-5 5" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;

// Tipos
interface Coupon {
    id: number;
    code: string;
    discount_type: 'percent' | 'fixed';
    discount_value: number | string;
    min_purchase_amount?: number | string;
    category_ids: number[];
    max_uses?: number;
    uses: number;
    expires_at?: string;
}

interface Category {
    id: number;
    name: string;
}

const initialCouponState: Partial<Coupon> = {
    code: '',
    discount_type: 'percent',
    discount_value: undefined,
    min_purchase_amount: undefined,
    category_ids: [],
    max_uses: undefined,
    expires_at: ''
};

export default function AdminCouponsPage() {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState<Partial<Coupon>>(initialCouponState);
    const [formStatus, setFormStatus] = useState({ loading: false, error: '' });
    const { showNotification } = useNotificationDispatch();

    const fetchCoupons = useCallback(async () => {
        try {
            const response = await apiClient.get('/admin/coupons');
            setCoupons(response.data);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao buscar cupons.', 'error');
        }
    }, [showNotification]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await apiClient.get('/admin/categories');
            setCategories(response.data);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao buscar categorias.', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        async function loadData() {
            setLoading(true);
            await Promise.all([fetchCoupons(), fetchCategories()]);
            setLoading(false);
        }
        loadData();
    }, [fetchCoupons, fetchCategories]);

    const handleEdit = (coupon: Coupon) => {
        setIsEditing(true);
        setCurrentCoupon({
            ...coupon,
            expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().substring(0, 16) : undefined
        });
    };

    const handleCreateNew = () => {
        setIsEditing(false);
        setCurrentCoupon(initialCouponState);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Tem certeza que deseja excluir este cupom?")) return;
        try {
            await apiClient.delete(`/admin/coupons/${id}`);
            showNotification('Cupom excluído com sucesso!', 'success');
            await fetchCoupons();
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao excluir o cupom.', 'error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus({ loading: true, error: '' });
        const payload = {
            ...currentCoupon,
            min_purchase_amount: Number(currentCoupon.min_purchase_amount) || null,
            max_uses: Number(currentCoupon.max_uses) || null,
        };

        try {
            if (isEditing) {
                await apiClient.put(`/admin/coupons/${currentCoupon.id}`, payload);
            } else {
                await apiClient.post('/admin/coupons', payload);
            }
            showNotification(`Cupom ${isEditing ? 'atualizado' : 'criado'} com sucesso!`, 'success');
            handleCreateNew();
            await fetchCoupons();
        } catch (err: any) {
            setFormStatus({ loading: false, error: err.response?.data?.error || 'Ocorreu um erro.' });
        } finally {
            setFormStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setCurrentCoupon(prev => ({ ...prev, [name]: value }));
    };

    const handleCategoryChange = (categoryId: number) => {
        const currentIds = currentCoupon.category_ids || [];
        const newIds = currentIds.includes(categoryId)
            ? currentIds.filter(id => id !== categoryId)
            : [...currentIds, categoryId];
        setCurrentCoupon(prev => ({ ...prev, category_ids: newIds }));
    };

    const getCategoryNames = (ids: number[]) => {
        if (!ids || ids.length === 0) return 'Todas';
        return ids.map(id => categories.find(c => c.id === id)?.name).filter(Boolean).join(', ');
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold text-text-primary">Gerenciar Cupons</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-lg shadow-subtle sticky lg:top-20">
                        <h2 className="text-xl font-bold text-text-primary mb-4 border-b pb-3">{isEditing ? 'Editar Cupom' : 'Adicionar Novo'}</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="form-label">Código</label>
                                <input type="text" name="code" value={currentCoupon.code || ''} onChange={handleChange} required className="form-input" placeholder="Ex: NATAL25" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="form-label">Tipo</label>
                                    <select name="discount_type" value={currentCoupon.discount_type} onChange={handleChange} className="form-input">
                                        <option value="percent">%</option>
                                        <option value="fixed">R$</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label">Valor</label>
                                    <input type="number" name="discount_value" value={currentCoupon.discount_value || ''} onChange={handleChange} required className="form-input" placeholder="Ex: 25" step="0.01" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="form-label">Compra Mínima</label>
                                    <input type="number" name="min_purchase_amount" value={currentCoupon.min_purchase_amount || ''} onChange={handleChange} className="form-input" placeholder="Opcional" step="0.01" />
                                </div>
                                <div>
                                    <label className="form-label">Máx. Usos</label>
                                    <input type="number" name="max_uses" value={currentCoupon.max_uses || ''} onChange={handleChange} className="form-input" placeholder="Opcional" />
                                </div>
                            </div>
                            <div>
                                <label className="form-label">Data de Expiração</label>
                                <input type="datetime-local" name="expires_at" value={currentCoupon.expires_at || ''} onChange={handleChange} className="form-input" />
                            </div>

                            <div>
                                <label className="form-label">Categorias Aplicáveis</label>
                                <p className="text-xs text-gray-500 mb-2">Se nenhuma for selecionada, o cupom será válido para todos os produtos.</p>
                                <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                                    {categories.map(cat => (
                                        <label key={cat.id} className="flex items-center space-x-2 p-1.5 rounded-md hover:bg-gray-100 cursor-pointer">
                                            <input type="checkbox" checked={(currentCoupon.category_ids || []).includes(cat.id)} onChange={() => handleCategoryChange(cat.id)} className="form-checkbox" />
                                            <span>{cat.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {formStatus.error && <p className="text-red-500 text-sm mt-2">{formStatus.error}</p>}

                            <button type="submit" disabled={formStatus.loading} className="w-full bg-brand-blue text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400">
                                {formStatus.loading ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Cupom')}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <h2 className="text-xl font-bold text-text-primary mb-4">Lista de Cupons</h2>
                    <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="th-cell">Código</th>
                                    <th className="th-cell">Desconto</th>
                                    <th className="th-cell">Compra Mín.</th>
                                    <th className="th-cell">Categorias</th>
                                    <th className="th-cell">Usos</th>
                                    <th className="th-cell">Expira em</th>
                                    <th className="th-cell text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={7} />)
                                ) : (
                                    coupons.map(coupon => (
                                        <tr key={coupon.id}>
                                            <td className="td-cell font-medium">{coupon.code}</td>
                                            <td className="td-cell">{coupon.discount_type === 'percent' ? `${parseFloat(String(coupon.discount_value))}%` : `R$ ${parseFloat(String(coupon.discount_value)).toFixed(2)}`}</td>
                                            <td className="td-cell">{coupon.min_purchase_amount ? `R$ ${parseFloat(String(coupon.min_purchase_amount)).toFixed(2)}` : '-'}</td>
                                            <td className="td-cell text-xs max-w-xs truncate">{getCategoryNames(coupon.category_ids)}</td>
                                            <td className="td-cell">{coupon.uses} / {coupon.max_uses || '∞'}</td>
                                            <td className="td-cell">{coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Nunca'}</td>
                                            <td className="td-cell text-right">
                                                <button onClick={() => handleEdit(coupon)} className="text-indigo-600 hover:text-indigo-900 p-1"><EditIcon /></button>
                                                <button onClick={() => handleDelete(coupon.id)} className="text-red-600 hover:text-red-900 ml-2 p-1"><TrashIcon /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}